/**
 * LayoutEmployee — top-level shell rendered under every `/my/*` route.
 *
 * Composes the Employee surface from four pieces:
 *  1. **`EmployeeSidebar`** — left rail. On small screens it collapses into
 *     a drawer driven by `isSideBarSmallScreenOpen`; on large screens it's
 *     always visible. `handleCollapsed` toggles the small-screen drawer.
 *  2. **`EmployeeNavbar`** — sticky top bar (search, help, notifications,
 *     profile menu, plus the "Back to admin" pill).
 *  3. **`<Outlet />`** — nested route content, rendered inside a centred
 *     `ContentInner` capped at `--layout-content-width`.
 *  4. **Auth gate** — while Frontegg's `isLoading` is true a spinner
 *     replaces the shell; the `<Outlet />` only mounts when
 *     `isAuthenticated` is true.
 *
 * Mirrors `LayoutPlatform` but is intentionally a separate file so the
 * Employee surface can diverge (different nav, different content widths,
 * future employee-only providers) without touching the platform shell.
 *
 * ## Responsive behaviour
 * `useIsSmallScreen` (below `md`) decides whether we are in small-screen
 * mode. On resize back to large, the effect closes any open small-screen
 * drawer so the sidebar does not double up.
 *
 * ## Layout tokens
 * All sizing uses new design-system tokens — no Tailwind, no MUI `sx`:
 *  - `--layout-nav-width`     sidebar width / content offset on desktop
 *  - `--layout-content-width` max width of the shell's content region
 *  - `--color-bg-surface`     page rail/background
 *  - `--color-bg-page`        scrollable content background
 *  - `--radius-lg`            content panel corner radius
 *
 * ## Usage
 * Mounted from `App.tsx` as the element of the `/my/*` route:
 *
 * ```tsx
 * <Route path="/my/*" element={<LayoutEmployee />}>
 *   <Route path="dashboard" element={<EmployeeDashboard />} />
 *   …
 * </Route>
 * ```
 */
import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useAuth } from "@frontegg/react";
import { CircularProgress } from "@mui/material";
import AppShell from "./AppShell";
import { useBrandingContext } from "context/BrandingContext";
import BackToAdminAction from "components/nav/BackToAdminAction";
import { useEmployeeSidebarItems } from "hooks/useEmployeeSidebarItems";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { MyScopeProvider } from "context/MyScopeContext";

/**
 * Renders the Employee shell. Takes no props — all state (auth status,
 * small-screen drawer) is sourced from contexts and hooks, so the layout
 * can be mounted directly by the router.
 */
const LayoutEmployee: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  const isSmallScreen = useIsSmallScreen();
  const { platform } = useBrandingContext();
  const footerDisclosure = platform?.footerDisclosure || "";
  const employeeNavItems = useEmployeeSidebarItems();
  const [isSideBarSmallScreenOpen, setIsSideBarSmallScreenOpen] =
    useState<boolean>(false);

  const handleCollapsed = () => {
    setIsSideBarSmallScreenOpen(!isSideBarSmallScreenOpen);
  };

  useEffect(() => {
    if (!isSmallScreen) setIsSideBarSmallScreenOpen(false);
  }, [isSmallScreen]);

  if (isLoading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress color="primary" />
      </div>
    );

  return (
    <MyScopeProvider>
      <AppShell
        basePath="/my"
        items={employeeNavItems}
        isSmallScreen={isSmallScreen}
        isSideBarSmallScreenOpen={isSideBarSmallScreenOpen}
        handleCollapsed={handleCollapsed}
        extraActions={<BackToAdminAction />}
        footerDisclosure={footerDisclosure}
      />
    </MyScopeProvider>
  );
};

export default LayoutEmployee;
